import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ListingsAPI } from '@certi/shared/api/listings/ListingsAPI'
import { getMockListingBySlug } from '@/config/mockListings'
import { BiddingAPI, type AuctionInfo, type BidHistoryResponse } from '@certi/shared/api/bidding/BiddingAPI'
import { OrderAPI, type ListingShippingEstimateResponse } from '@certi/shared/api/order/OrderAPI'
import { UserAPI } from '@certi/shared/api/user/UserAPI'
import { FavoriteAPI } from '@certi/shared/api/user/FavoriteAPI'
import { FavoriteType } from '@certi/shared/types/types.user'
import type { ListingResponse } from '@certi/shared/types'
import { useBiddingWebSocket, type BidUpdateMessage } from '../../hooks/useBiddingWebSocket'
import { useAppConfig } from '@/hooks/useAppConfig'
import { useAuth } from '@/hooks/useAuth'
import { AuthModal } from '@/components/auth'
import { signInWithSocialProvider } from '@/lib/socialAuth'
import { FaHeart, FaRegHeart } from 'react-icons/fa'

interface GameDetails {
  date: string
  venue: string
  opponent: string
}

const MAX_BID_HISTORY_ITEMS = 100

const formatBidInputAmount = (amount: number) => {
  const normalizedAmount = Number.isFinite(amount) ? amount : 0
  return Number.isInteger(normalizedAmount) ? normalizedAmount.toString() : normalizedAmount.toFixed(2)
}

const roundUpToCents = (amount: number) => Math.ceil(amount * 100) / 100

const isUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)

const getQuickBidAmount = (auctionInfo: AuctionInfo, percentage: number) =>
  Math.max(
    auctionInfo.minimumNextBid,
    roundUpToCents(auctionInfo.currentPrice * (1 + percentage / 100))
  )

const isAuctionBiddable = (auctionInfo: AuctionInfo | null, timeRemaining: string) =>
  !!auctionInfo &&
  auctionInfo.status === 'ACTIVE' &&
  auctionInfo.isActive &&
  timeRemaining !== 'Ended'

export default function ListingDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { user, signIn, signUp, signInWithGoogle, signInWithApple, accessToken, session } = useAuth()
  const { config: appConfig } = useAppConfig()
  const [listing, setListing] = useState<ListingResponse | null>(null)
  const [auctionInfo, setAuctionInfo] = useState<AuctionInfo | null>(null)
  const [bidHistory, setBidHistory] = useState<BidHistoryResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [timeRemaining, setTimeRemaining] = useState<string>('')
  const [bidAmount, setBidAmount] = useState<string>('')
  const [placingBid, setPlacingBid] = useState(false)
  const [_isWebSocketConnected, setIsWebSocketConnected] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showAllBids, setShowAllBids] = useState(false)
  const [bidFeedback, setBidFeedback] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null)
  const [isFavorite, setIsFavorite] = useState(false)
  const [favoriteLoading, setFavoriteLoading] = useState(false)
  const [estimateExpanded, setEstimateExpanded] = useState(false)
  const [shippingEstimate, setShippingEstimate] = useState<ListingShippingEstimateResponse | null>(null)
  const [shippingEstimateLoading, setShippingEstimateLoading] = useState(false)
  const [shippingEstimateError, setShippingEstimateError] = useState<string | null>(null)
  const token = accessToken ?? session?.accessToken

  const redirectToProfileForBidding = () => {
    navigate('/profile', { state: { reason: 'complete-bidding-eligibility' } })
  }

  const syncAuctionState = (auctionResponse: AuctionInfo) => {
    setAuctionInfo(auctionResponse)
    setBidAmount(prev => {
      const parsedAmount = Number.parseFloat(prev)
      if (!prev || Number.isNaN(parsedAmount) || parsedAmount < auctionResponse.minimumNextBid) {
        return formatBidInputAmount(auctionResponse.minimumNextBid)
      }
      return prev
    })
  }

  const refreshAuctionState = async (listingId: string) => {
    const auctionResponse = await BiddingAPI.getListingAuction(listingId)
    syncAuctionState(auctionResponse)

    const bidHistoryResponse = await BiddingAPI.getBidHistory(
      auctionResponse.auctionId,
      MAX_BID_HISTORY_ITEMS,
      0
    )
    setBidHistory(bidHistoryResponse)
  }

  // Handle real-time bid updates via WebSocket
  const handleBidUpdate = (message: BidUpdateMessage) => {
    console.log('Bid update received:', message)
    
    if ((message.type === 'bid_placed' || message.type === 'auction_ended') && message.auctionData) {
      const nextMinimumBid = message.auctionData.minimumNextBid
      const normalizedNextMinimumBid = Number.isFinite(nextMinimumBid)
        ? nextMinimumBid
        : roundUpToCents(message.auctionData.currentPrice + 1)

      // Update auction info with new current price and bid count
      setAuctionInfo(prev => prev ? {
        ...prev,
        currentPrice: message.auctionData!.currentPrice,
        minimumNextBid: normalizedNextMinimumBid,
        isActive: message.auctionData!.isActive ?? prev.isActive,
        status: message.auctionData!.status,
        endsAt: message.auctionData!.endsAt,
        winningPrice: message.auctionData!.winningPrice ?? prev.winningPrice
      } : null)

      setBidAmount(prev => {
        const parsedAmount = Number.parseFloat(prev)
        if (!prev || Number.isNaN(parsedAmount) || parsedAmount < normalizedNextMinimumBid) {
          return formatBidInputAmount(normalizedNextMinimumBid)
        }
        return prev
      })
      
      // Add new bid to history
      if (message.bidData) {
        const newBidHistoryItem: BidHistoryResponse = {
          bidderId: message.bidData.bidderId,
          amount: message.bidData.amount,
          placedAt: message.bidData.placedAt,
          status: message.bidData.status
        }
        setBidHistory(prev => [newBidHistoryItem, ...prev].slice(0, MAX_BID_HISTORY_ITEMS))
      }

      if (message.type === 'auction_ended') {
        setTimeRemaining('Ended')
        setBidFeedback({
          type: 'info',
          message: 'This auction has ended. Bidding is now closed.',
        })
      }
    }
  }

  // Initialize WebSocket connection
  const { isConnected: _isConnected } = useBiddingWebSocket({
    auctionId: auctionInfo?.auctionId,
    onBidUpdate: handleBidUpdate,
    onConnectionChange: setIsWebSocketConnected
  })

  // Countdown timer effect
  useEffect(() => {
    const updateCountdown = () => {
      if (!auctionInfo) return
      
      const timeLeft = BiddingAPI.getTimeRemaining(auctionInfo.endsAt)
      setTimeRemaining(timeLeft)
    }

    if (auctionInfo) {
      updateCountdown()
      const timer = setInterval(updateCountdown, 1000)
      return () => clearInterval(timer)
    }
  }, [auctionInfo])

  useEffect(() => {
    if (!slug) {
      navigate('/marketplace')
      return
    }

    const fetchListingData = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Fetch listing details
        let listingResponse: ListingResponse | null = null
        try {
          listingResponse = await ListingsAPI.getListingBySlug(slug)
        } catch (e) {
          if (isUuid(slug)) {
            listingResponse = await ListingsAPI.getListingById(slug)
          } else {
            const enableMock = (import.meta as any).env?.VITE_ENABLE_MOCK_LISTINGS === 'true'
            if (enableMock) {
              const mock = getMockListingBySlug(slug)
              if (mock) {
                setListing(mock)
                setLoading(false)
                return
              }
            }
            throw e
          }
        }
        if (!listingResponse) {
          const enableMock = (import.meta as any).env?.VITE_ENABLE_MOCK_LISTINGS === 'true'
          if (enableMock) {
            const mock = getMockListingBySlug(slug)
            if (mock) {
              setListing(mock)
              setLoading(false)
              return
            }
          }
          throw new Error('Listing not found')
        }
        setListing(listingResponse)
        
        // Fetch auction information if listing exists
        if (listingResponse?.id) {
          try {
            await refreshAuctionState(listingResponse.id)
          } catch (auctionErr) {
            console.warn('No auction found for listing:', auctionErr)
            // Not all listings have auctions, this is okay
            setAuctionInfo(null)
            setBidHistory([])
            setBidAmount('')
          }
        }
        
      } catch (err) {
        console.error('Failed to fetch listing:', err)
        setError(err instanceof Error ? err.message : 'Failed to load listing')
      } finally {
        setLoading(false)
      }
    }

    fetchListingData()
  }, [slug, navigate])

  useEffect(() => {
    if (!listing?.id || !user?.id || !token) {
      setIsFavorite(false)
      setFavoriteLoading(false)
      return
    }

    let cancelled = false
    setIsFavorite(false)
    setFavoriteLoading(true)

    const loadFavoriteState = async () => {
      try {
        const isFavorited = await FavoriteAPI.checkFavorite(
          user.id.toString(),
          FavoriteType.LISTING,
          listing.id,
          token
        )
        if (!cancelled) {
          setIsFavorite(isFavorited)
        }
      } catch (err) {
        console.error('Failed to load favorite state:', err)
      } finally {
        if (!cancelled) {
          setFavoriteLoading(false)
        }
      }
    }

    loadFavoriteState()

    return () => {
      cancelled = true
    }
  }, [listing?.id, user?.id, token])

  useEffect(() => {
    if (!listing?.id || !auctionInfo) {
      setShippingEstimate(null)
      setShippingEstimateError(null)
      setShippingEstimateLoading(false)
      return
    }

    const parsedBidAmount = Number.parseFloat(bidAmount)
    const estimatedBidAmount = Number.isFinite(parsedBidAmount)
      ? Math.max(parsedBidAmount, auctionInfo.minimumNextBid)
      : auctionInfo.minimumNextBid

    if (!Number.isFinite(estimatedBidAmount) || estimatedBidAmount <= 0) {
      setShippingEstimate(null)
      setShippingEstimateError(null)
      setShippingEstimateLoading(false)
      return
    }

    if (!user?.id || !token) {
      setShippingEstimate(null)
      setShippingEstimateError('Sign in to estimate shipping')
      setShippingEstimateLoading(false)
      return
    }

    let cancelled = false
    setShippingEstimateLoading(true)
    setShippingEstimateError(null)

    const timer = window.setTimeout(async () => {
      try {
        const estimate = await OrderAPI.estimateListingShipping(listing.id, estimatedBidAmount, token)
        if (!cancelled) {
          setShippingEstimate(estimate)
        }
      } catch (err) {
        if (!cancelled) {
          setShippingEstimate(null)
          setShippingEstimateError(err instanceof Error ? err.message : 'Unable to estimate shipping')
        }
      } finally {
        if (!cancelled) {
          setShippingEstimateLoading(false)
        }
      }
    }, 400)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [listing?.id, auctionInfo?.minimumNextBid, bidAmount, user?.id, token])

  // Game details from API (currently not implemented)
  const getGameDetails = (_listing: ListingResponse): GameDetails | null => {
    // Return null until game details are properly stored in the database
    return null
  }


  // Helper functions (removed unused formatCurrency)

  const getTagStyles = (tag: string) => {
    switch (tag) {
      case 'AUTOGRAPHED':
        return { color: 'text-blue-700', bg: 'bg-blue-100' }
      case 'LIMITED_EDITION':
        return { color: 'text-purple-700', bg: 'bg-purple-100' }
      case 'PLAYOFF_GAME':
        return { color: 'text-green-700', bg: 'bg-green-100' }
      case 'FINAL_MATCH':
        return { color: 'text-red-700', bg: 'bg-red-100' }
      case 'DEBUT_GAME':
        return { color: 'text-orange-700', bg: 'bg-orange-100' }
      case 'MVP_GAME':
        return { color: 'text-yellow-700', bg: 'bg-yellow-100' }
      default:
        return { color: 'text-gray-700', bg: 'bg-gray-100' }
    }
  }


  const handleFastBid = async (percentage: number) => {
    // Check if user is authenticated
    if (!user) {
      setShowAuthModal(true)
      return
    }

    // Check if user is verified/eligible to bid
    if (user && token) {
      try {
        const eligibility = await UserAPI.getBiddingEligibility(user.id, token)
        if (!eligibility.eligible) {
          redirectToProfileForBidding()
          return
        }
      } catch (err) {
        console.error('Failed to check bidding eligibility:', err)
        // If eligibility check fails, allow the bid attempt (backend will validate)
      }
    }

    if (!auctionInfo) return
    if (!isAuctionBiddable(auctionInfo, timeRemaining)) {
      setBidFeedback({
        type: 'info',
        message: auctionInfo.status === 'PENDING'
          ? 'This auction is not live yet. Please wait for it to start.'
          : 'This auction is no longer accepting bids.',
      })
      return
    }

    try {
      setPlacingBid(true)
      setBidFeedback(null)

      const bidAmountNum = getQuickBidAmount(auctionInfo, percentage)

      await BiddingAPI.placeBid({
        auctionId: auctionInfo.auctionId,
        amount: bidAmountNum
      })

      if (listing?.id) {
        await refreshAuctionState(listing.id)
      }
      setBidFeedback({
        type: 'success',
        message: 'Bid placed successfully.',
      })

    } catch (err) {
      console.error('Failed to place bid:', err)
      setBidFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to place bid',
      })
    } finally {
      setPlacingBid(false)
    }
  }

  const handlePlaceBid = async () => {
    // Check if user is authenticated
    if (!user) {
      setShowAuthModal(true)
      return
    }

    // Check if user is verified/eligible to bid
    if (user && token) {
      try {
        const eligibility = await UserAPI.getBiddingEligibility(user.id, token)
        if (!eligibility.eligible) {
          redirectToProfileForBidding()
          return
        }
      } catch (err) {
        console.error('Failed to check bidding eligibility:', err)
        // If eligibility check fails, allow the bid attempt (backend will validate)
      }
    }

    if (!auctionInfo || !bidAmount) return
    if (!isAuctionBiddable(auctionInfo, timeRemaining)) {
      setBidFeedback({
        type: 'info',
        message: auctionInfo.status === 'PENDING'
          ? 'This auction is not live yet. Please wait for it to start.'
          : 'This auction is no longer accepting bids.',
      })
      return
    }

    try {
      setPlacingBid(true)
      setBidFeedback(null)

      const bidAmountNum = parseFloat(bidAmount)
      if (bidAmountNum < auctionInfo.minimumNextBid) {
        setBidFeedback({
          type: 'error',
          message: `Bid must be at least ${BiddingAPI.formatCurrency(auctionInfo.minimumNextBid)}`,
        })
        return
      }

      await BiddingAPI.placeBid({
        auctionId: auctionInfo.auctionId,
        amount: bidAmountNum
      })

      if (listing?.id) {
        await refreshAuctionState(listing.id)
      }
      setBidFeedback({
        type: 'success',
        message: 'Bid placed successfully.',
      })

    } catch (err) {
      console.error('Failed to place bid:', err)
      setBidFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to place bid',
      })
    } finally {
      setPlacingBid(false)
    }
  }

  const handleSocialLogin = async (provider: 'google' | 'apple', credential?: string) => {
    await signInWithSocialProvider(
      provider,
      { signInWithGoogle, signInWithApple },
      credential
    )
  }

  const handleToggleFavorite = async () => {
    if (!listing?.id) return

    if (!user?.id || !token) {
      setShowAuthModal(true)
      return
    }

    if (favoriteLoading) return

    const previousFavorite = isFavorite
    setFavoriteLoading(true)
    setIsFavorite(!previousFavorite)

    try {
      const isFavorited = await FavoriteAPI.toggleFavorite(
        user.id.toString(),
        { targetType: FavoriteType.LISTING, targetId: listing.id },
        token
      )
      setIsFavorite(isFavorited)
    } catch (err) {
      console.error('Failed to toggle favorite:', err)
      setIsFavorite(previousFavorite)
    } finally {
      setFavoriteLoading(false)
    }
  }

  const handlePrevImage = () => {
    if (listing?.mediaUrls && listing.mediaUrls.length > 1) {
      setCurrentImageIndex(prev => 
        prev === 0 ? listing.mediaUrls.length - 1 : prev - 1
      )
    }
  }

  const handleNextImage = () => {
    if (listing?.mediaUrls && listing.mediaUrls.length > 1) {
      setCurrentImageIndex(prev => 
        prev === listing.mediaUrls.length - 1 ? 0 : prev + 1
      )
    }
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-200 aspect-square rounded-lg"></div>
            <div className="space-y-6">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-12 bg-gray-200 rounded w-1/2"></div>
              <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !listing) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {error === 'Listing not found' ? 'Listing Not Found' : 'Error Loading Listing'}
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/marketplace')}
            className="btn btn-primary"
          >
            Back to Marketplace
          </button>
        </div>
      </div>
    )
  }

  // Component functions for better organization
  const gameDetails = listing ? getGameDetails(listing) : null
  const hasMultipleImages = listing?.mediaUrls && listing.mediaUrls.length > 1

  // Media Viewer Component - Desktop Layout with Carousel
  const MediaViewer = () => (
    <div className="w-full">
      {hasMultipleImages ? (
        /* Desktop Layout: Thumbnails + Main Image */
        <div className="flex gap-4">
          {/* Left: Thumbnail Carousel */}
          <div className="flex flex-col space-y-3 w-20">
            {listing?.mediaUrls?.map((url, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                  index === currentImageIndex 
                    ? 'border-brand-primary shadow-md' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <img
                  src={url}
                  alt={`${listing.title} ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

          {/* Right: Main Image */}
          <div className="flex-1">
            <div className="relative aspect-[4/3] bg-gray-100 rounded-xl overflow-hidden shadow-lg">
              <img
                src={listing?.mediaUrls?.[currentImageIndex]}
                alt={listing?.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  target.parentElement!.innerHTML = `
                    <div class="w-full h-full flex items-center justify-center bg-gray-100">
                      <svg class="w-16 h-16 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                      </svg>
                    </div>
                  `
                }}
              />
              
              {/* Navigation arrows */}
              <button
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-75 transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-75 transition-all"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Single Image Layout */
        <div className="relative aspect-[4/3] bg-gray-100 rounded-xl overflow-hidden shadow-lg">
          {listing?.mediaUrls && listing.mediaUrls.length > 0 ? (
            <img
              src={listing.mediaUrls[0]}
              alt={listing.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
                target.parentElement!.innerHTML = `
                  <div class="w-full h-full flex items-center justify-center bg-gray-100">
                    <svg class="w-16 h-16 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                    </svg>
                  </div>
                `
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-16 h-16 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
              </svg>
            </div>
          )}
        </div>
      )}
    </div>
  )

  // Item Title Section Component
  const ItemTitleSection = () => (
    <div className="space-y-4">
      {listing?.teamOrClub && (
        <p className="text-lg text-gray-600 font-medium">{listing.teamOrClub}</p>
      )}
      <div className="flex items-start gap-4">
        <h1 className="flex-1 text-4xl font-bold text-gray-900 leading-tight">
          {listing?.title}
        </h1>
        <button
          type="button"
          onClick={handleToggleFavorite}
          disabled={favoriteLoading}
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          className={`grid h-11 w-11 flex-shrink-0 place-items-center rounded-full border shadow-sm transition-colors ${
            isFavorite
              ? 'border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100'
              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
          } disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {isFavorite ? <FaHeart className="h-5 w-5" /> : <FaRegHeart className="h-5 w-5" />}
        </button>
      </div>
      {listing?.playerName && (
        <p className="text-2xl font-semibold text-gray-800">
          {listing.playerName}
        </p>
      )}

      {/* Badges - mobile style */}
      <div className="flex flex-wrap gap-2">
        {/* Certi Authenticated Badge */}
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          ✓ Certi Authenticated
        </span>
        {/* Dynamic tags */}
        {listing?.tags?.map((tag) => {
          const styles = getTagStyles(tag)
          return (
            <span
              key={tag}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles.color} ${styles.bg}`}
            >
              {tag.replace(/_/g, ' ')}
            </span>
          )
        })}
      </div>
    </div>
  )

  // Enhanced Bid Section Component 
  const BidSection = () => {
    if (!auctionInfo) {
      return (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg">
          <div className="text-center py-8">
            <p className="text-gray-600 text-lg">No auction available for this item</p>
            <p className="text-sm text-gray-500 mt-2">This item may be available for direct purchase</p>
          </div>
        </div>
      )
    }

    const auctionCanBid = isAuctionBiddable(auctionInfo, timeRemaining)
    const auctionStartingSoon = auctionInfo.status === 'PENDING' && timeRemaining !== 'Ended'
    const visibleBidHistory = showAllBids ? bidHistory : bidHistory.slice(0, 3)
    const parsedBidAmount = Number.parseFloat(bidAmount)
    const estimatedBidAmount = Number.isFinite(parsedBidAmount)
      ? Math.max(parsedBidAmount, auctionInfo.minimumNextBid)
      : auctionInfo.minimumNextBid
    const estimatedShippingAmount = shippingEstimate?.selectedRate.amount ?? null
    const estimatedMaxTotal = estimatedShippingAmount === null
      ? null
      : estimatedBidAmount + estimatedShippingAmount
    
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg">
        <div className="space-y-6">
          {/* Current Bid */}
          <div>
            <p className="text-gray-600 text-lg font-medium mb-2">Current Bid</p>
            <p className="text-5xl font-bold text-gray-900">
              {BiddingAPI.formatCurrency(auctionInfo.currentPrice)}
            </p>
          </div>

          {/* Auction Meta */}
          <div className="flex items-center justify-between text-gray-600">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx={12} cy={12} r={10} />
                <polyline points="12,6 12,12 16,14" />
              </svg>
              <span className="font-medium">
                Ends {timeRemaining || 'Loading...'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx={9} cy={7} r={4} />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span className="font-medium">
                {bidHistory.length} bid{bidHistory.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {bidFeedback && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                bidFeedback.type === 'success'
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : bidFeedback.type === 'error'
                    ? 'border-red-200 bg-red-50 text-red-800'
                    : 'border-amber-200 bg-amber-50 text-amber-800'
              }`}
            >
              {bidFeedback.message}
            </div>
          )}

          {auctionStartingSoon && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              This auction is scheduled and will open shortly.
            </div>
          )}

          {/* Fast Bid Buttons */}
          {auctionCanBid && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Bid
              </label>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <button
                  onClick={() => handleFastBid(5)}
                  disabled={placingBid}
                  className="py-2 px-4 rounded-lg text-sm font-semibold transition-all border-2 border-brand-primary bg-transparent text-brand-primary hover:bg-[var(--brand-primary)] hover:!text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {BiddingAPI.formatCurrency(getQuickBidAmount(auctionInfo, 5))}
                </button>
                <button
                  onClick={() => handleFastBid(15)}
                  disabled={placingBid}
                  className="py-2 px-4 rounded-lg text-sm font-semibold transition-all border-2 border-brand-primary bg-transparent text-brand-primary hover:bg-[var(--brand-primary)] hover:!text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {BiddingAPI.formatCurrency(getQuickBidAmount(auctionInfo, 15))}
                </button>
                <button
                  onClick={() => handleFastBid(25)}
                  disabled={placingBid}
                  className="py-2 px-4 rounded-lg text-sm font-semibold transition-all border-2 border-brand-primary bg-transparent text-brand-primary hover:bg-[var(--brand-primary)] hover:!text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {BiddingAPI.formatCurrency(getQuickBidAmount(auctionInfo, 25))}
                </button>
              </div>
            </div>
          )}

          {/* Bid Input (without label) */}
          {auctionCanBid && (
            <div>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-500">$</span>
                <input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  min={auctionInfo.minimumNextBid}
                  step="0.01"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  placeholder="Enter bid amount"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Minimum bid: {BiddingAPI.formatCurrency(auctionInfo.minimumNextBid)}
              </p>
            </div>
          )}

          {/* Place Bid Button */}
          <button
            onClick={handlePlaceBid}
            disabled={!auctionCanBid || placingBid}
            className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-300 ${
              auctionCanBid && !placingBid
                ? 'bg-brand-primary text-white hover:bg-brand-primary/90 shadow-lg hover:shadow-xl hover:scale-105'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {placingBid ? 'Placing Bid...' :
             auctionStartingSoon ? 'Auction Starts Soon' :
             timeRemaining === 'Ended' || !auctionInfo.isActive ? 'Auction Ended' :
             'Place Bid'}
          </button>

          <div className="rounded-xl border border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={() => setEstimateExpanded(prev => !prev)}
              className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left"
              aria-expanded={estimateExpanded}
            >
              <div>
                <p className="text-sm font-medium text-gray-600">Estimated total</p>
                <p className="text-xl font-bold text-gray-900">
                  {shippingEstimateLoading
                    ? 'Calculating...'
                    : estimatedMaxTotal !== null
                      ? BiddingAPI.formatCurrency(estimatedMaxTotal)
                      : 'Unavailable'}
                </p>
              </div>
              <svg
                className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform ${estimateExpanded ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <polyline points="6,9 12,15 18,9" />
              </svg>
            </button>

            {estimateExpanded && (
              <div className="space-y-3 border-t border-gray-200 px-4 py-4 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-gray-600">Max bid</span>
                  <span className="font-semibold text-gray-900">{BiddingAPI.formatCurrency(estimatedBidAmount)}</span>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
                  <div className="min-w-0">
                    <p className="text-gray-600">Estimated shipping</p>
                    {shippingEstimate?.originSummary && (
                      <p className="mt-1 text-xs text-gray-500">{shippingEstimate.originSummary}</p>
                    )}
                  </div>
                  <span className="whitespace-nowrap text-right font-semibold text-gray-900">
                    {shippingEstimateLoading
                      ? 'Calculating...'
                      : estimatedShippingAmount !== null
                        ? BiddingAPI.formatCurrency(estimatedShippingAmount)
                        : 'Unavailable'}
                  </span>
                </div>
                {/* TODO: Add location-based sales tax here once tax calculation is available. */}
                <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-3">
                  <span className="font-medium text-gray-700">Estimated max total</span>
                  <span className="font-bold text-gray-900">
                    {estimatedMaxTotal !== null ? BiddingAPI.formatCurrency(estimatedMaxTotal) : 'Unavailable'}
                  </span>
                </div>
                {shippingEstimateError && (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {shippingEstimateError}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Bid History */}
          {bidHistory.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Latest Bids</h3>

              {/* Column Headers */}
              <div className="grid grid-cols-3 gap-4 pb-2 border-b border-gray-200">
                <p className="text-xs text-gray-500 font-medium">Bidder</p>
                <p className="text-xs text-gray-500 font-medium">Date</p>
                <p className="text-xs text-gray-500 font-medium text-right">Bid</p>
              </div>

              {/* Bid Rows */}
              <div className="space-y-0">
                {visibleBidHistory.map((bid, index) => {
                  const date = new Date(bid.placedAt)
                  const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  const formattedTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })

                  return (
                    <div key={index} className="grid grid-cols-3 gap-4 py-3 border-b border-gray-100 last:border-b-0">
                      <p className="text-sm font-medium text-gray-900">
                        {bid.bidderId.toString().slice(0, 8)}
                      </p>
                      <p className="text-xs text-gray-700">
                        {formattedDate}, {formattedTime}
                      </p>
                      <p className="text-sm font-bold text-gray-900 text-right">
                        {BiddingAPI.formatCurrency(bid.amount)}
                      </p>
                    </div>
                  )
                })}
              </div>

              {bidHistory.length > 3 && (
                <button
                  onClick={() => setShowAllBids(prev => !prev)}
                  className="w-full mt-4 py-2 px-4 text-sm font-medium text-brand-primary border border-brand-primary rounded-lg hover:bg-brand-primary/5 transition-colors"
                >
                  {showAllBids ? 'Show latest 3 bids' : `See all ${bidHistory.length} bids`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Description Block Component
  const DescriptionBlock = () => {
    if (!listing?.description) return null
    
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Description</h3>
        <p className="text-gray-700 leading-relaxed text-lg">
          {listing.description}
        </p>
        {appConfig.nfcMockMode && listing?.id && (
          <a
            href={`/verify?listingId=${listing.id}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-green-700 hover:text-green-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Verify authenticity
          </a>
        )}
      </div>
    )
  }

  // Game Details Section Component (mobile-inspired)
  const GameDetailsSection = () => {
    if (!gameDetails) return null
    
    const DetailRow = ({ icon, label, value }: { icon: string, label: string, value: string }) => (
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 w-6 h-6 text-gray-500 mt-1">
          <div dangerouslySetInnerHTML={{ __html: icon }} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500 mb-1">{label}</p>
          <p className="text-lg font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    )

    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Game Details</h3>
        <div className="space-y-6">
          <DetailRow
            icon={`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`}
            label="Game Date"
            value={gameDetails.date}
          />
          <DetailRow
            icon={`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`}
            label="Venue"
            value={gameDetails.venue}
          />
          <DetailRow
            icon={`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`}
            label="Opponent"
            value={gameDetails.opponent}
          />
        </div>
      </div>
    )
  }

  // Player Stats Section Component (mobile-inspired)
  const PlayerStatsSection = () => {
    if (!listing?.playerName) return null
    
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Player Stats</h3>
        <div className="text-center py-8 space-y-3">
          <p className="text-lg font-medium text-gray-600">
            Player statistics will be available soon
          </p>
          <p className="text-sm text-gray-500">
            {listing.playerName} career highlights coming soon
          </p>
        </div>
      </div>
    )
  }

  // Item Details Component
  const ItemDetailsSection = () => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Item Details</h3>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-600 font-medium">Category</span>
          <span className="font-semibold text-gray-900">{listing?.category.replace('_', ' ')}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600 font-medium">Condition</span>
          <span className="font-semibold text-gray-900">{listing?.condition.replace('_', ' ')}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600 font-medium">Sport</span>
          <span className="font-semibold text-gray-900">{listing?.sportType}</span>
        </div>
        {listing?.size && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">Size</span>
            <span className="font-semibold text-gray-900">{listing.size}</span>
          </div>
        )}
        {listing?.leagueOrEvent && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">League/Event</span>
            <span className="font-semibold text-gray-900">{listing.leagueOrEvent}</span>
          </div>
        )}
        {listing?.dateOrSeason && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600 font-medium">Date/Season</span>
            <span className="font-semibold text-gray-900">{listing.dateOrSeason}</span>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/marketplace')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Marketplace</span>
          </button>
        </div>
      </div>

      {/* Main Content - Desktop Layout */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Top Section: Image Gallery + Bidding */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Left: Image Gallery (2/3 width) */}
          <div className="lg:col-span-2">
            <MediaViewer />
          </div>

          {/* Right: Bidding Section (1/3 width) */}
          <div className="lg:col-span-1">
            {/* Item Title Section */}
            <div className="mb-6">
              <ItemTitleSection />
            </div>
            
            {/* Bid Section */}
            <div className="sticky top-24">
              <BidSection />
            </div>
          </div>
        </div>

        {/* Bottom Section: Info Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Description */}
          <div className="lg:col-span-2">
            <DescriptionBlock />
          </div>

          {/* Game Details */}
          <div className="lg:col-span-1">
            <GameDetailsSection />
          </div>
        </div>

        {/* Additional Info Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Player Stats */}
          <PlayerStatsSection />

          {/* Item Details */}
          <ItemDetailsSection />
        </div>

        {/* Bottom Padding */}
        <div className="h-12" />
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLogin={async (email: string, password: string) => {
          await signIn({ email, password })
        }}
        onRegister={async (email: string, password: string, name: string) => {
          const [firstName, ...lastNameParts] = name.split(' ')
          const lastName = lastNameParts.join(' ') || firstName
          await signUp({ firstName, lastName, email, password })
        }}
        onSocialLogin={handleSocialLogin}
      />
    </div>
  )
}
